import maplibregl from "maplibre-gl";
import { Protocol } from "pmtiles";
import "maplibre-gl/dist/maplibre-gl.css";
import "./styles.css";
import { ParcelStore, normalizeFips } from "./parcel-store.js";
import {
  bindBasemapButtons,
  renderConfigError,
  renderEmptyState,
  renderMapError,
  renderParcelDetails,
  renderParcelError,
  setActiveBasemap,
  setStatus
} from "./ui.js";

const NO_MATCH = "__none__";
const CONFIG_URL = import.meta.env.VITE_DATASET_CONFIG_URL || "/config/dataset.json";

function toAbsoluteUrl(value, baseUrl) {
  if (typeof value !== "string" || !value.trim()) {
    return value;
  }
  try {
    return new URL(value, baseUrl).toString();
  } catch {
    return value;
  }
}

function normalizeConfigUrls(config, configUrl) {
  const baseUrl = configUrl || window.location.href;
  if (config?.tiles?.pmtilesUrl) {
    config.tiles.pmtilesUrl = toAbsoluteUrl(config.tiles.pmtilesUrl, baseUrl);
  }
  if (config?.attributes?.baseUrl) {
    config.attributes.baseUrl = toAbsoluteUrl(config.attributes.baseUrl, baseUrl);
  }
  if (config?.attributes?.manifestUrl) {
    config.attributes.manifestUrl = toAbsoluteUrl(config.attributes.manifestUrl, baseUrl);
  }
  return config;
}

function formatParcelSourceError(error, pmtilesUrl) {
  const rawMessage = error instanceof Error ? error.message : String(error ?? "");
  if (/\b404\b/.test(rawMessage)) {
    return `Parcel tiles URL returned 404: ${pmtilesUrl}`;
  }
  if (rawMessage) {
    return `Parcel tiles failed to load from ${pmtilesUrl}. ${rawMessage}`;
  }
  return `Parcel tiles failed to load from ${pmtilesUrl}.`;
}

async function loadConfig() {
  const response = await fetch(CONFIG_URL, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Missing config at ${CONFIG_URL}.`);
  }
  const config = await response.json();
  const resolvedConfigUrl =
    response.url || new URL(CONFIG_URL, window.location.href).toString();
  return normalizeConfigUrls(config, resolvedConfigUrl);
}

function resolveParcelMinZoom(config) {
  const value = Number(config?.tiles?.minZoom);
  if (Number.isFinite(value) && value >= 0 && value <= 22) {
    return value;
  }
  return 12;
}

function resolveBasemaps(config) {
  const defaults = {
    streets: {
      label: "Streets",
      url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      tileSize: 256,
      paint: {
        "raster-opacity": 0.9,
        "raster-saturation": -0.6
      }
    },
    satellite: {
      label: "Satellite",
      url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      attribution:
        'Tiles &copy; Esri, Maxar, Earthstar Geographics, and the GIS User Community',
      tileSize: 256,
      paint: {
        "raster-opacity": 1,
        "raster-saturation": 0
      }
    }
  };

  const configured = config.basemaps && typeof config.basemaps === "object"
    ? config.basemaps
    : config.basemap
      ? { streets: config.basemap }
      : {};

  return {
    streets: {
      ...defaults.streets,
      ...(configured.streets ?? {})
    },
    satellite: {
      ...defaults.satellite,
      ...(configured.satellite ?? {})
    }
  };
}

function buildStyle(config) {
  const initial = config.initialView ?? {
    center: [-99.2, 31.05],
    zoom: 9
  };
  const parcelMinZoom = resolveParcelMinZoom(config);
  const basemaps = resolveBasemaps(config);

  return {
    mapOptions: {
      center: initial.center,
      zoom: initial.zoom,
      minZoom: 4,
      maxZoom: 19
    },
    parcelMinZoom,
    basemaps,
    defaultBasemap: "streets",
    style: {
      version: 8,
      glyphs: "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf",
      sources: {
        basemapStreets: {
          type: "raster",
          tiles: [basemaps.streets.url],
          tileSize: basemaps.streets.tileSize,
          attribution: basemaps.streets.attribution
        },
        basemapSatellite: {
          type: "raster",
          tiles: [basemaps.satellite.url],
          tileSize: basemaps.satellite.tileSize,
          attribution: basemaps.satellite.attribution
        },
        parcels: {
          type: "vector",
          url: `pmtiles://${config.tiles.pmtilesUrl}`
        }
      },
      layers: [
        {
          id: "background",
          type: "background",
          paint: {
            "background-color": "#f5f5f0"
          }
        },
        {
          id: "basemap-streets",
          type: "raster",
          source: "basemapStreets",
          layout: {
            visibility: "visible"
          },
          paint: basemaps.streets.paint
        },
        {
          id: "basemap-satellite",
          type: "raster",
          source: "basemapSatellite",
          layout: {
            visibility: "none"
          },
          paint: basemaps.satellite.paint
        },
        {
          id: "parcel-hit",
          type: "fill",
          source: "parcels",
          "source-layer": config.tiles.sourceLayer,
          minzoom: parcelMinZoom,
          paint: {
            "fill-color": "#132015",
            "fill-opacity": 0
          }
        },
        {
          id: "parcel-outline",
          type: "line",
          source: "parcels",
          "source-layer": config.tiles.sourceLayer,
          minzoom: parcelMinZoom,
          paint: {
            "line-color": "#c66a1a",
            "line-width": [
              "interpolate",
              ["linear"],
              ["zoom"],
              9,
              0.15,
              12,
              0.35,
              16,
              0.8
            ],
            "line-opacity": 0.62
          }
        },
        {
          id: "parcel-hover",
          type: "line",
          source: "parcels",
          "source-layer": config.tiles.sourceLayer,
          minzoom: parcelMinZoom,
          filter: ["==", ["get", "parcel_id"], NO_MATCH],
          paint: {
            "line-color": "#f08a24",
            "line-width": 1.4,
            "line-opacity": 0.98
          }
        },
        {
          id: "parcel-selected-fill",
          type: "fill",
          source: "parcels",
          "source-layer": config.tiles.sourceLayer,
          minzoom: parcelMinZoom,
          filter: ["==", ["get", "parcel_id"], NO_MATCH],
          paint: {
            "fill-color": "#f29b38",
            "fill-opacity": 0.16
          }
        },
        {
          id: "parcel-selected-outline",
          type: "line",
          source: "parcels",
          "source-layer": config.tiles.sourceLayer,
          minzoom: parcelMinZoom,
          filter: ["==", ["get", "parcel_id"], NO_MATCH],
          paint: {
            "line-color": "#d97706",
            "line-width": 1.6
          }
        }
      ]
    }
  };
}

function parcelFromFeature(feature) {
  const properties = feature?.properties ?? {};
  return {
    parcel_id: properties.parcel_id ?? null,
    fips: normalizeFips(properties.fips),
    county: properties.county ?? null,
    owner_name: properties.owner_name ?? null
  };
}

function setParcelFilter(map, layerId, parcelId) {
  map.setFilter(layerId, ["==", ["get", "parcel_id"], parcelId ?? NO_MATCH]);
}

function setBasemap(map, name) {
  const layerNames = ["streets", "satellite"];
  for (const key of layerNames) {
    map.setLayoutProperty(
      `basemap-${key}`,
      "visibility",
      key === name ? "visible" : "none"
    );
  }
  setActiveBasemap(name);
}

async function bootstrap() {
  try {
    const config = await loadConfig();
    if (!config?.tiles?.pmtilesUrl) {
      throw new Error("Missing tiles.pmtilesUrl in dataset config.");
    }
    if (!config?.tiles?.sourceLayer) {
      throw new Error("Missing tiles.sourceLayer in dataset config.");
    }
    const store = new ParcelStore(config.attributes);
    const protocol = new Protocol();
    maplibregl.addProtocol("pmtiles", protocol.tile);

    const built = buildStyle(config);
    const map = new maplibregl.Map({
      container: "map",
      style: built.style,
      ...built.mapOptions
    });

    renderEmptyState(built.parcelMinZoom);
    setStatus("Loading map...");

    let selectedParcelId = null;
    let hoveredParcelId = null;
    let sourceLoadFailed = false;

    const updateFilters = () => {
      setParcelFilter(map, "parcel-hover", hoveredParcelId);
      setParcelFilter(map, "parcel-selected-fill", selectedParcelId);
      setParcelFilter(map, "parcel-selected-outline", selectedParcelId);
    };

    map.on("load", () => {
      setBasemap(map, built.defaultBasemap);
      bindBasemapButtons((name) => setBasemap(map, name));
      setStatus(`Ready. Zoom to ${built.parcelMinZoom}+ and click any parcel.`);
    });

    map.on("error", (event) => {
      if (sourceLoadFailed) {
        return;
      }
      const rawMessage = event?.error?.message ?? "";
      const isParcelSourceError =
        event?.sourceId === "parcels" ||
        /pmtiles/i.test(rawMessage) ||
        /Bad response code/i.test(rawMessage);
      if (!isParcelSourceError) {
        return;
      }

      sourceLoadFailed = true;
      const message = formatParcelSourceError(event?.error, config.tiles.pmtilesUrl);
      renderMapError(message);
      setStatus("Map data failed to load.");
      console.error("Parcel source error:", event?.error);
    });

    map.on("mousemove", "parcel-hit", (event) => {
      const feature = event.features?.[0];
      const next = feature?.properties?.parcel_id ?? null;
      if (hoveredParcelId !== next) {
        hoveredParcelId = next;
        updateFilters();
      }
      map.getCanvas().style.cursor = "pointer";
    });

    map.on("mouseleave", "parcel-hit", () => {
      hoveredParcelId = null;
      updateFilters();
      map.getCanvas().style.cursor = "";
    });

    map.on("click", "parcel-hit", async (event) => {
      const feature = event.features?.[0];
      const summary = parcelFromFeature(feature);
      if (!summary.parcel_id || !summary.fips) {
        renderParcelError("Parcel key is missing from tile attributes.");
        return;
      }

      selectedParcelId = summary.parcel_id;
      updateFilters();
      setStatus("Loading parcel details...");

      try {
        const details = await store.lookupParcel(summary.parcel_id, summary.fips);
        if (details) {
          renderParcelDetails(details);
          setStatus("Parcel details loaded.");
        } else {
          renderParcelDetails(summary);
          setStatus("No extended record found. Showing tile summary.");
        }
      } catch (error) {
        renderParcelError(error.message);
        setStatus("Lookup failed.");
      }
    });
  } catch (error) {
    renderConfigError(error.message);
    setStatus("Configuration error.");
  }
}

void bootstrap();
